import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { style: {"width":"100%","overflow":"auto"} }

import moment from "moment";
import { useStore } from "vuex";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { formatMonthQuery } from "@/core/helpers/bao-cao-pdf-params";
import { openBaoCaoPdf } from "@/components/shared/pdf-viewer/open-bao-cao-pdf";
import {
  IKhoResponseBase,
  ITongHopVatTuResponseBase,
} from "@/core/interfaces/ApiResponses";
import { KhoService } from "@/core/services/Kho.service";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { swalErrNotification } from "@/core/helpers/utils";
import { ButtonsType, ButtonTypeColors } from "@/core/enums/buttons.enums";
import { BaoCaoService } from "@/core/services/BaoCao.service";
import ReportType from "@/core/enums/ReportTypes.enums";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import TongHopVatTuPrintModal from "./print-modals/TongHopVatTuPrintModal.vue";
import TongHopVatTuTable from "./table-components/TongHopVatTuTable.vue";
import useFormInput from "@/core/hooks/use-form-input";


export default /*#__PURE__*/_defineComponent({
  __name: 'TongHopVatTu',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const isTongHopNhap = computed(() =>
  route.path.includes(ReportType.TONG_HOP_NHAP_VAT_TU)
);
("");

const header = computed(() =>
  isTongHopNhap.value ? "Tổng hợp nhập vật tư" : "Tổng hợp xuất vật tư"
);

setCurrentPageTitle(header.value);

setBreadcrumbLinks([
  {
    text: "Báo cáo",
  },
  {
    text: header.value,
  },
]);

const { startLoading, endLoading } = useLoading();

const store = useStore();

const currentYear = computed(() => store.getters.getSelectedYear);

const { formRef } = useCreatingForm();

const { defaultDate, disableDate } = useDateTime();

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  khoId: -1,
});

const khoItems = ref<IKhoResponseBase[]>([]);

const fetchKhoItems = async () => {
  startLoading();

  try {
    const {
      data: {
        data: { data },
      },
    } = await KhoService.fetchAllWarehouses();

    khoItems.value = data;

    endLoading();
  } catch (e) {
    endLoading();

    swalErrNotification(e, "Có lỗi khi lấy danh sách kho");
  }
};

const fromDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.to && moment(value).isAfter(form.value.to)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const toDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.from && moment(value).isBefore(form.value.from)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const rules = ref({
  from: [
    {
      validator: fromDateValidation,
      trigger: "change",
    },
  ],
  to: [
    {
      validator: toDateValidation,
      trigger: "change",
    },
  ],
  khoId: [
    {
      type: "number",
      message: "Phải chọn kho",
      trigger: "change",
      min: 1,
    },
  ],
});

onMounted(() => {
  nextTick(async () => {
    await fetchKhoItems();
  });
});

const selectingKho = computed(() => {
  if (!form.value.khoId) return null;

  return khoItems.value.find(({ id }) => id === form.value.khoId);
});

const isFiltersChanging = ref(true);

const items = ref<ITongHopVatTuResponseBase[]>([]);

const { focusFirstErrInputV2 } = useFormInput();

const generate = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid) => {
    if (isValid) {
      try {
        startLoading();

        const tongHop = await BaoCaoService.getTongHopVatTu({
          khoId: form.value.khoId ?? 0,
          isTongHopNhap: isTongHopNhap.value,
          from: form.value.from.getMonth() + 1,
          to: form.value.to.getMonth() + 1,
        });
        items.value = tongHop.items;

        endLoading();

        isFiltersChanging.value = false;
      } catch (e) {
        await endLoading();

        swalErrNotification(e, "Có lỗi xảy ra");
      }
    } else {
      await focusFirstErrInputV2();

      return false;
    }
  });
};

const showPrintButton = computed(() => {
  if (isFiltersChanging.value) return false;

  if (!items.value.length) return false;

  if (!selectingKho.value || !form.value.from || !form.value.to) return false;

  return true;
});

const printModalRef = ref();

const openPdfViewer = () => {
  openBaoCaoPdf(router, {
    reportType: isTongHopNhap.value
      ? ReportType.TONG_HOP_NHAP_VAT_TU
      : ReportType.TONG_HOP_XUAT_VAT_TU,
    from: formatMonthQuery(form.value.from),
    to: formatMonthQuery(form.value.to),
    khoId: form.value.khoId,
    isTongHopNhap: isTongHopNhap.value,
  });
};

watch(
  () => route.name,
  () => {
    isFiltersChanging.value = true;
    document.title = `${
      isTongHopNhap.value ? "Tổng hợp nhập vật tư" : "Tổng hợp xuất vật tư"
    } - Đường sắt Hà Nội`;
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(TongHopVatTuPrintModal, {
      ref_key: "printModalRef",
      ref: printModalRef,
      from: new Date(form.value.from) ?? null,
      to: new Date(form.value.to) ?? null,
      "is-tong-hop-nhap": isTongHopNhap.value,
      items: items.value,
      kho: selectingKho.value ?? null,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (printModalRef.value.isOpen = false))
    }, null, 8, ["from", "to", "is-tong-hop-nhap", "items", "kho"]),
    _createVNode(PageContentBase, { plain: "" }, {
      default: _withCtx(() => [
        _createVNode(PageContentSection, { "always-expanded": true }, {
          header: _withCtx(() => [
            _createTextVNode(_toDisplayString(header.value), 1)
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_form, {
              ref_key: "formRef",
              ref: formRef,
              model: form.value,
              rules: rules.value,
              onSubmit: _withModifiers(generate, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Kho:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "khoId",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_select, {
                          modelValue: form.value.khoId,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.khoId) = $event)),
                          class: "w-100",
                          name: "khoId"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_option, {
                              label: "Chưa chọn",
                              value: -1
                            }),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(khoItems.value, (kho) => {
                              return (_openBlock(), _createBlock(_component_el_option, {
                                key: kho.id,
                                label: kho.ten_kho,
                                value: kho.id
                              }, null, 8, ["label", "value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, { class: "mb-5" }, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Từ tháng:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "from",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_date_picker, {
                          modelValue: form.value.from,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.from) = $event)),
                          class: "w-100",
                          "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                          type: "month",
                          placeholder: "Từ",
                          name: "from",
                          format: "MM/YYYY"
                        }, null, 8, ["modelValue", "disabled-date"])
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Đến tháng:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "to",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_date_picker, {
                          modelValue: form.value.to,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.to) = $event)),
                          class: "w-100",
                          "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                          placeholder: "Đến",
                          name: "to",
                          format: "MM/YYYY",
                          type: "month"
                        }, null, 8, ["modelValue", "disabled-date"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(DivRow, { class: "mb-4" }, {
                  default: _withCtx(() => [
                    _createVNode(DivCol, { cols: 8 }),
                    _createVNode(DivCol, {
                      cols: 4,
                      class: "d-flex justify-content-end"
                    }, {
                      default: _withCtx(() => [
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 0,
                              "button-type": _unref(ButtonsType).PRINT,
                              onClick: openPdfViewer
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("In phiếu")
                              ]),
                              _: 1
                            }, 8, ["button-type"]))
                          : _createCommentVNode("", true),
                        _createVNode(ButtonCustom, {
                          "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                          "color-type": _unref(ButtonTypeColors).SUCCESS,
                          "tooltip-message": "Xuất báo cáo",
                          onClick: generate
                        }, null, 8, ["button-type", "color-type"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model", "rules"]),
            _createElementVNode("div", _hoisted_1, [
              _createVNode(TongHopVatTuTable, {
                "is-print-modal": false,
                "is-tong-hop-nhap": isTongHopNhap.value,
                items: items.value,
                stripe: true
              }, null, 8, ["is-tong-hop-nhap", "items"])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})